import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { IActionHandler } from "@preveil-api";
import {
  Account, DriveLimits, DriveStarredErrorMessages, DriveStarredMessages, GenericEntryEntity, GenericListEntity, GlobalErrorMessages, Message, MessageAnchors, MessageHandlerDisplayType,
  MessageToastTypes, Pagination, useAppSelector, useFetchStarred, useStarNode, DriveErrorMessages
} from "src/common";
import { EmptyView, Loading, PageHeader } from "src/components";
import { ListView, Toolbar } from ".";
import { driveActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _, { isNumber } from "lodash";

type AllProps = {
  current_account: Account;
};

// Description: This component is the main entry point for the Drive Starred section, managing starred entries and their actions.
// PRD Link: https://preveil.atlassian.net/wiki/spaces/FEAT/pages/edit-v2/4236705798
// "starred" is called "favorites" in the backend because:
// We use "starred" already in a mail context, and that's handled differently: https://github.com/PreVeil/core/blob/dev/collection_server/docs/mail.md#get-mailcidstarred_threads
// The term "favorite" hasn't been used yet on the backend side, so there's no conceptual collision with that term.
// The term "starred" seems more like a visual artifact than a description of what it is used for -- if we decide to present these items differently on the frontend in the future (say as "bulleted" or "checked" etc), I don't think we'd want the backend APIs to change to match.
function DriveStarredComponent(props: AllProps) {
  const { current_account } = props;
  const dispatch = useDispatch();
  const selected = useAppSelector((state: RootState) => state.drive.selected);
  const pagination = useAppSelector((state: RootState) => state.drive.pagination);
  const [filter, setFilter] = useState<string>("");
  const [filter_count, setFilterCount] = useState<number>(0);
  const [is_loading, setIsLoading] = useState(true);
  const [error_message, setErrorMessage] = useState<string | undefined>();
  const [page, setPage] = useState<string>(DriveLimits.DRIVE_PAGE_DEFAULT);
  const { starred_list, error, fetchStarredList, resetStarredList } = useFetchStarred(current_account);
  const { setBulkUnStarState, reset, success, error: error_unstarring, clearAllStarredNodes, star } = useStarNode(current_account);

  useEffect(() => {
    fetchStarredList();
    return () => {
      setPage(DriveLimits.DRIVE_PAGE_DEFAULT);
      dispatch(driveActions.resetPagination());
      dispatch(driveActions.setSelected([]));
      dispatch(driveActions.resetCurrentPathInfo());
    };
  }, []);

  useEffect(() => {
    if (!!starred_list) {
      DriveStarredRequests.handleStarNodesSuccess(starred_list);
      dispatch(driveActions.setLoading(false));
      setIsLoading(false);
      resetStarredList();
    }

    if (!!error) {
      setErrorMessage(DriveStarredErrorMessages.starred_list_failed);
      dispatch(driveActions.setLoading(false));
      setIsLoading(false);
      resetStarredList();
    }
  }, [starred_list, error]);

  useEffect(() => {
    success && handleSuccess(!!star ? star.name : undefined);

    if (!!error_unstarring) {
      setErrorMessage(DriveStarredErrorMessages.error_unstarring_bulk_nodes);
      dispatch(driveActions.setLoading(false));
      setIsLoading(false);
      reset();
    }
  }, [success, error_unstarring, star]);

  // Description: Handle Success after toggle - If bulk action and there are failures then pass a warning
  function handleSuccess(name?: string) {
    const msg = !!name ? DriveStarredMessages.success_unstar_state.replace(MessageAnchors.file_name, name) : DriveStarredMessages.success_unstar_bulk_nodes;
    dispatch(uiActions.handleSetMessage(new Message(msg, MessageHandlerDisplayType.toastr, MessageToastTypes.success)));
    DriveStarredRequests.handleRefresh();
    reset();
  }

  // Description: Handle all children component actions and store it
  const DriveStarredRequests = {
    handleRefresh: (reset_page: boolean = false) => {
      setIsLoading(true);
      reset_page && ResetPage();
      fetchStarredList(Number(reset_page ? DriveLimits.DRIVE_PAGE_DEFAULT : page));
      !!selected.length && dispatch(driveActions.setSelected([]));
    },
    //  Description: Run after fetching starred_list
    handleStarNodesSuccess(_starred_list: GenericListEntity) {
      const total_page_rows = _starred_list.entries.length;
      const total_count = starred_list?.pagination_info.total_count;
      const total_rows = total_count || _starred_list.entries.length;
      if (total_page_rows > 0 && total_rows > 0) {
        const pagination_item = new Pagination(Pagination.getPageIndex(page), total_page_rows, total_rows, pagination.pageSize).pagination_item;
        !!pagination_item && dispatch(driveActions.setPagination(pagination_item));
      } else {
        dispatch(driveActions.resetPagination());
      }
    },
    // Description: Handles select all from List View header
    handleSelect(params: { entry?: GenericEntryEntity, selected: boolean }) {
      let new_selected = selected.slice();
      const _id = !!params.entry ? params.entry.id : null;
      if (!!_id) { // Note: individual check
        if (params.selected) {
          !_.find(new_selected, (_selected: string) => (_selected === _id)) &&
            new_selected.push(_id);
        } else {
          const index = _.findIndex(new_selected, (_selected: string) => (_selected === _id));
          index >= 0 && (new_selected.splice(index, 1));
        }
      } else {
        new_selected = params.selected ? _.map(starred_list?.entries, (entry: GenericEntryEntity) => (entry.id)) : [];
      }
      dispatch(driveActions.setSelected(new_selected));
    },
    //  Description: Handle unwatch from menu or toolbar
    handleUnstar(ids: string[]) {
      if (starred_list?.entries) {
        const entries = _.compact(_.map(ids, (id: string) => _.find(starred_list.entries, (entry) => entry.id === id)));
        entries.length > 0 ? setBulkUnStarState(entries) : DriveStarredRequests.handleRefresh();
      } else {
        DriveStarredRequests.handlePageErrorMessage({
          message: GlobalErrorMessages.default, stack: {
            error: DriveStarredErrorMessages.error_setting_node_star
          }
        });
      }
    },
    handleClearAllStarred() {
      clearAllStarredNodes();
    },
    handleFilter: setFilter,
    //  Description: Navigate to page passed
    handlePaging(new_page: string) {
      if (new_page !== page && isNumber(new_page)) {
        setIsLoading(true);
        fetchStarredList(Number(new_page));
        setPage(new_page);
      } else {
        DriveStarredRequests.handlePageErrorMessage({
          message: GlobalErrorMessages.default, stack: {
            error: DriveErrorMessages.error_paginating.replace(MessageAnchors.page_number, new_page)
          }
        });
      }
    },
    handlePageErrorMessage: (params: { message: string, stack?: any; }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.logger), params.stack));
    }
  };

  //  Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    dispatch(uiActions.handleMessageDismiss());
    const callback = `handle${action_obj.actionType}`;
    // Handle local calls:
    if ((DriveStarredRequests as any)[callback] instanceof Function) {
      (DriveStarredRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      DriveStarredRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  // Description: Reset state supporting client side pagination
  function ResetPage() {
    setPage(DriveLimits.DRIVE_PAGE_DEFAULT);
    dispatch(driveActions.resetPagination());
  }

  return <>
    <PageHeader>
      <h1 className="mb-3">Starred</h1>
    </PageHeader>
    <Toolbar is_loading={is_loading} filter_count={filter_count} handleAction={handlePageActions} pagination={pagination} selected={selected} counts={starred_list?.entries.length}></Toolbar>
    <div className="cover-content">
      {
        !!starred_list ?
          <ListView
            current_account={current_account}
            is_loading={is_loading}
            selected={selected}
            filter={filter}
            setFilterCount={setFilterCount}
            starred_list={starred_list}
            pagination={pagination}
            handleAction={handlePageActions} /> :
          is_loading ? <Loading className="in-place mt-5" /> :
            <EmptyView message={(!!error_message ? error_message : "No Starred Items")} />
      }
    </div>
  </>;
}

export default React.memo(DriveStarredComponent);
